import { useContext, useEffect, useState } from 'react';
import { UserUpdate } from '../model/models';
import { Step } from '../model/wizard/wizard';
import { Auth } from '../services/AuthService';
import { AppContext } from '../state/AppContextProvider';
import { ActionType } from '../state/GlobalState';
import { useUserInformations } from './WPHooks';

export const useWizard = () => {
  const { state, dispatch } = useContext(AppContext);

  const [loading, isSetLoading] = useState<boolean>(true);

  const { update, user } = useUserInformations();

  const [doneSteps, setDoneSteps] = useState<{ [x: string]: boolean }>(
    (user?.acf.wizard || []).reduce((accumulator, t) => {
      return { ...accumulator, [t.slug]: true };
    }, {}) || {}
  );

  const WIZARDSTEPS: Array<Step> = [
    'language',
    'photo',
    'cursus',
    'notifications',
    'annuaire',
    'interests',
    'done',
  ];

  const showWizard: () => boolean = () => {
    return state.wizardSteps.length > 0;
  };

  const getStepToDo: () => Promise<Step[]> = async () => {
    if (user) {
      const isResident = user.acf.user_gia_type === 'resident';

      // remove cursus step for non resident
      const allSteps = WIZARDSTEPS.filter(
        (step) => isResident || step !== 'cursus'
      );

      if (user.acf.wizard) {
        const userWizard = user.acf.wizard.map((step) => step.slug);
        const isStepNotif =
          'true' === (await Auth.load(`${user.username}@wizard_notifications`));
        if (isStepNotif) {
          userWizard.push('notifications' as Step);
        }
        const stepToDo = allSteps.filter((step) => !userWizard.includes(step));
        if (stepToDo === undefined) {
          return [];
        }
        if (stepToDo.length > 1 || state.wizardSteps.length > 0) {
          return stepToDo;
        } else {
          return [];
        }
      } else {
        return allSteps;
      }
    } else {
      return [];
    }
  };

  const getStepIndex = (stepName: string): number => {
    return WIZARDSTEPS.findIndex((step) => {
      return step === stepName;
    });
  };

  const getNextStep = (currentStep: string) => {
    const currentStepIndex = getStepIndex(currentStep);
    if (currentStepIndex < WIZARDSTEPS.length) {
      const nextStepIndex = currentStepIndex + 1;
      let nextStep: string = WIZARDSTEPS[nextStepIndex];
      if (user && user.acf.user_gia_type !== 'resident') {
        if (nextStep === 'cursus') {
          const currentStepIndexCursus = getStepIndex(nextStep);
          if (currentStepIndexCursus < WIZARDSTEPS.length) {
            const stepAfterCursus = nextStepIndex + 1;
            nextStep = WIZARDSTEPS[stepAfterCursus];
          } else {
            nextStep = '';
          }
        }
      }
      return nextStep;
    } else {
      return '';
    }
  };

  const getPreviousStep = (currentStep: string) => {
    const currentStepIndex = getStepIndex(currentStep);
    if (currentStepIndex > 0) {
      const previousStepIndex = currentStepIndex - 1;
      return WIZARDSTEPS[previousStepIndex];
    } else {
      return '';
    }
  };

  const isFirstStep = (currentStep: string): boolean => {
    const firstItem = WIZARDSTEPS[0];
    return firstItem === currentStep ? true : false;
  };

  const isLastStep = (currentStep: string): boolean => {
    const lastItem = WIZARDSTEPS.slice(-1)[0];
    return lastItem === currentStep ? true : false;
  };

  const saveStep = (currentStep: string) => {
    if (user) {
      const newDoneSteps = { ...doneSteps, [currentStep]: true };
      setDoneSteps(newDoneSteps);

      if (currentStep === 'notifications') {
        return Auth.save(`${user.username}@wizard_${currentStep}`, 'true').then(
          () => true
        );
      } else {
        const data: UserUpdate = {
          acf: {
            wizard: Object.entries(newDoneSteps).map((e) => ({
              slug: e[0] as Step,
            })),
          },
        };
        return update(data).catch(() => {
          return false;
        });
      }
    }
  };

  useEffect(() => {
    if (state.isConnected && state.user && !state.user.offline) {
      getStepToDo().then((step) => {
        dispatch({
          type: ActionType.SetWizardSteps,
          payload: step,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isConnected, state.user]);

  return {
    wizardSteps: state.wizardSteps,
    getNextStep: getNextStep,
    saveStep: saveStep,
    getPreviousStep: getPreviousStep,
    showWizard: showWizard,
    isFirstStep: isFirstStep,
    isLastStep: isLastStep,
    getStepIndex: getStepIndex,
    WIZARDSTEPS: WIZARDSTEPS,
    loading: loading,
    isSetLoading: isSetLoading,
  };
};
